import React, { cloneElement } from 'react';
import Swal from 'sweetalert2';
import { ImportButton } from 'react-admin-import-csv';
import {
    AutocompleteInput,
    BooleanField,
    List,
    TextField,
    ShowButton,
    CreateButton,
    ExportButton,
    Filter,
    SelectInput,
    ReferenceInput,
    useListContext,
    TopToolbar,
    sanitizeListRestProps,
    FunctionField,
    Link,
    ChipField,
    DateField,
} from 'react-admin';
import CustomSearchInput from '../components/CustomSearchInput';
import BoxitPagination from '../components/BoxitPagination';
import UserReferenceField from '../components/UserReferenceField';
import { StorageStatus } from '../constants';
import capitalize from 'capitalize';
import CustomizableGrid from '../components/CustomizableGrid';

const StorageTypeFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />
        <ReferenceInput
            source="userId"
            reference="users"
            resource="users"
            label="Customer"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={user => (user.name ? user.name : user.email)}
            />
        </ReferenceInput>
        <SelectInput
            source="inventory"
            label="Type"
            choices={[
                {
                    id: true,
                    name: 'Inventory',
                },
                {
                    id: false,
                    name: 'Not Inventory',
                },
            ]}
        />
        <SelectInput
            source="status"
            label="Status"
            choices={Object.keys(StorageStatus).map(status => ({
                id: status,
                name: capitalize(status.replace('_', ' ')),
            }))}
        />
    </Filter>
);
// Custom field component for customer name with link
const CustomerNameField = ({ record }) => {
    if (record && record.user && record.user.name) {
        return (
            <Link to={`/users/${record.user.id}/show`}>{record.user.name}</Link>
        );
    } else if (record && record.userId) {
        return <UserReferenceField record={record} label="Customer" />;
    }
    return null;
};

const ListActions = props => {
    const { className, exporter, filters, maxResults, ...rest } = props;
    const {
        currentSort,
        resource,
        displayedFilters,
        filterValues,
        basePath,
        showFilter,
        total,
    } = useListContext();
    return (
        <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
            {filters &&
                cloneElement(filters, {
                    resource,
                    showFilter,
                    displayedFilters,
                    filterValues,
                    context: 'button',
                })}
            <ImportButton
                {...props}
                logging={false}
                dynamicTyping={true}
                transformRows={(csvRows: any[]) => {
                    return csvRows.map(i => ({
                        ...i,
                        inventory: true,
                        partnerId: 1,
                    }));
                }}
                validateRow={(csvRowItem: any) => {
                    let missing: string[] = [];
                    if (!csvRowItem.code) missing.push('code');
                    if (!csvRowItem.name) missing.push('name');
                    if (!csvRowItem.size) missing.push('size');
                    if (!csvRowItem.userId) missing.push('userId');
                    if (!csvRowItem.partnerId) missing.push('partnerId');
                    if (!csvRowItem.inventory) missing.push('inventory');

                    if (missing.length) {
                        const error = `Missing fields: ${missing.join(', ')}`;
                        Swal.fire('Validation Error', error, 'error');
                        throw new Error(error);
                    }
                }}
                postCommitCallback={(error: any) => {
                    if (error.filter(row => !row.success).length) {
                        const msg = error
                            .map(
                                (i, index) =>
                                    `
<tr>
<td>${index + 1}. </td>
<td>${i.err.errors.map(e => e.value + ': ' + e.message).join(', ')}
</td>
</tr>`
                            )
                            .join('');

                        Swal.fire('Error', `<table>${msg}</table>`, 'error');
                    }
                }}
            />
            <ExportButton
                disabled={total === 0}
                resource={resource}
                sort={currentSort}
                filterValues={filterValues}
                maxResults={maxResults}
            />
            <CreateButton basePath={basePath} />
        </TopToolbar>
    );
};

// Custom field components
const PartnerField = ({ record }) => {
    if (record && record.partner && record.partner.name) {
        return (
            <Link to={`/partners/${record.partner.id}/show`}>
                {record.partner.name}
            </Link>
        );
    }
    return null;
};

const StatusField = ({ record }) => {
    // If inventory is true, get status from first storage record
    if (
        record &&
        record.inventory === true &&
        record.storages &&
        record.storages.length > 0 &&
        record.storages[0].status
    ) {
        const statusValue = record.storages[0].status;
        return (
            <ChipField
                record={{
                    status: capitalize(statusValue.replace('_', ' ')),
                }}
                source="status"
                color={
                    statusValue === 'active'
                        ? 'primary'
                        : statusValue === 'delivered'
                        ? 'secondary'
                        : statusValue === 'in'
                        ? 'success'
                        : statusValue === 'stored'
                        ? 'info'
                        : statusValue === 'closed'
                        ? 'error'
                        : 'default'
                }
            />
        );
    }
    // Otherwise, use the storage type's own status
    else if (record && record.status) {
        const statusValue = record.status;
        return (
            <ChipField
                record={{
                    status: capitalize(statusValue.replace('_', ' ')),
                }}
                source="status"
                color={
                    statusValue === 'active'
                        ? 'primary'
                        : statusValue === 'delivered'
                        ? 'secondary'
                        : statusValue === 'in'
                        ? 'success'
                        : statusValue === 'stored'
                        ? 'info'
                        : statusValue === 'closed'
                        ? 'error'
                        : 'default'
                }
            />
        );
    }
    return null;
};

const QuantityField = ({ record }) => {
    if (record && record.inventory === true) {
        const quantity = record?.storages?.[0]?.storedQty;
        return quantity != null ? quantity : 'N/A';
    }
    return 'N/A';
};

const StorageTypeList = props => {
    const defaultColumns = [
        // Default visible columns (essential information)
        {
            id: 'serial',
            label: 'Serial',
            visible: true,
            sortable: false,
        },
        {
            id: 'id',
            label: 'ID',
            visible: true,
        },
        {
            id: 'code',
            label: 'Code',
            visible: true,
        },
        {
            id: 'name',
            label: 'Name',
            visible: true,
        },
        {
            id: 'size',
            label: 'CBM',
            visible: true,
        },
        {
            id: 'partner',
            label: 'Partner',
            visible: true,
        },
        {
            id: 'customer',
            label: 'Customer',
            visible: true,
        },
        {
            id: 'inventory',
            label: 'Inventory',
            visible: true,
        },
        {
            id: 'quantity',
            label: 'Quantity',
            visible: true,
        },
        {
            id: 'status',
            label: 'Status',
            visible: true,
        },
        {
            id: 'show',
            label: 'Show',
            visible: true,
            sortable: false,
        },

        // Optional columns (visible: false, can be enabled by users)
        {
            id: 'createdAt',
            label: 'Created At',
            visible: false,
        },
        {
            id: 'updatedAt',
            label: 'Updated At',
            visible: false,
        },
        {
            id: 'description',
            label: 'Description',
            visible: false,
        },
        {
            id: 'length',
            label: 'Length',
            visible: false,
        },
        {
            id: 'width',
            label: 'Width',
            visible: false,
        },
        {
            id: 'height',
            label: 'Height',
            visible: false,
        },
        {
            id: 'consumedSize',
            label: 'Consumed Size',
            visible: false,
        },
        {
            id: 'tracking',
            label: 'Tracking',
            visible: false,
        },
        {
            id: 'userId',
            label: 'User ID',
            visible: false,
        },
        {
            id: 'partnerId',
            label: 'Partner ID',
            visible: false,
        },
        {
            id: 'countryIds',
            label: 'Country IDs',
            visible: false,
        },
        {
            id: 'old_countryIds',
            label: 'Old Country IDs',
            visible: false,
        },
        {
            id: 'partnerLogoUrl',
            label: 'Partner Logo URL',
            visible: false,
        },
        {
            id: 'partnerCountryId',
            label: 'Partner Country ID',
            visible: false,
        },
        {
            id: 'partnerUserId',
            label: 'Partner User ID',
            visible: false,
        },
        {
            id: 'userEmail',
            label: 'Customer Email',
            visible: false,
        },
        {
            id: 'userPhone',
            label: 'Customer Phone',
            visible: false,
        },
        {
            id: 'userType',
            label: 'Customer Type',
            visible: false,
        },
        {
            id: 'userCountryId',
            label: 'Customer Country ID',
            visible: false,
        },
        {
            id: 'storageCount',
            label: 'Storage Count',
            visible: false,
        },
        {
            id: 'firstStorageCode',
            label: 'First Storage Code',
            visible: false,
        },
        {
            id: 'firstStorageName',
            label: 'First Storage Name',
            visible: false,
        },
    ];

    const renderColumn = columnId => {
        switch (columnId) {
            case 'serial':
                return <TextField source="serial" sortable={false} />;
            case 'id':
                return <TextField source="id" />;
            case 'code':
                return <TextField source="code" />;
            case 'name':
                return <TextField source="name" />;
            case 'size':
                return <TextField source="size" label="CBM" />;
            case 'partner':
                return (
                    <FunctionField
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'customer':
                return (
                    <FunctionField
                        label="Customer"
                        render={record => <CustomerNameField record={record} />}
                    />
                );
            case 'inventory':
                return <BooleanField source="inventory" />;
            case 'quantity':
                return (
                    <FunctionField
                        label="Quantity"
                        render={record => <QuantityField record={record} />}
                    />
                );
            case 'status':
                return (
                    <FunctionField
                        label="Status"
                        render={record => <StatusField record={record} />}
                    />
                );
            case 'show':
                return <ShowButton />;
            case 'createdAt':
                return <DateField source="createdAt" showTime />;
            case 'updatedAt':
                return <DateField source="updatedAt" showTime />;
            case 'description':
                return <TextField source="description" />;
            case 'length':
                return <TextField source="length" />;
            case 'width':
                return <TextField source="width" />;
            case 'height':
                return <TextField source="height" />;
            case 'consumedSize':
                return <TextField source="consumedSize" />;
            case 'tracking':
                return <BooleanField source="tracking" />;
            case 'userId':
                return <TextField source="userId" />;
            case 'partnerId':
                return <TextField source="partnerId" />;
            case 'countryIds':
                return (
                    <FunctionField
                        label="Country IDs"
                        render={record =>
                            record.countryIds
                                ? record.countryIds.join(', ')
                                : ''
                        }
                    />
                );
            case 'old_countryIds':
                return (
                    <FunctionField
                        label="Old Country IDs"
                        render={record =>
                            record.old_countryIds
                                ? record.old_countryIds.join(', ')
                                : ''
                        }
                    />
                );
            case 'partnerLogoUrl':
                return (
                    <FunctionField
                        label="Partner Logo URL"
                        render={record =>
                            record.partner ? record.partner.logoUrl : ''
                        }
                    />
                );
            case 'partnerCountryId':
                return (
                    <FunctionField
                        label="Partner Country ID"
                        render={record =>
                            record.partner ? record.partner.countryId : ''
                        }
                    />
                );
            case 'partnerUserId':
                return (
                    <FunctionField
                        label="Partner User ID"
                        render={record =>
                            record.partner ? record.partner.userId : ''
                        }
                    />
                );
            case 'userEmail':
                return (
                    <FunctionField
                        label="Customer Email"
                        render={record =>
                            record.user ? record.user.email : ''
                        }
                    />
                );
            case 'userPhone':
                return (
                    <FunctionField
                        label="Customer Phone"
                        render={record =>
                            record.user ? record.user.phone : ''
                        }
                    />
                );
            case 'userType':
                return (
                    <FunctionField
                        label="Customer Type"
                        render={record => {
                            if (record.user && record.user.type) {
                                return (
                                    <ChipField
                                        record={{
                                            type: record.user.type.toUpperCase(),
                                        }}
                                        source="type"
                                        color={
                                            record.user.type === 'b2c'
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                    />
                                );
                            }
                            return '';
                        }}
                    />
                );
            case 'userCountryId':
                return (
                    <FunctionField
                        label="Customer Country ID"
                        render={record =>
                            record.user ? record.user.countryId : ''
                        }
                    />
                );
            case 'storageCount':
                return (
                    <FunctionField
                        label="Storage Count"
                        render={record =>
                            record.storages ? record.storages.length : 0
                        }
                    />
                );
            case 'firstStorageCode':
                return (
                    <FunctionField
                        label="First Storage Code"
                        render={record =>
                            record.storages && record.storages.length > 0
                                ? record.storages[0].code || ''
                                : ''
                        }
                    />
                );
            case 'firstStorageName':
                return (
                    <FunctionField
                        label="First Storage Name"
                        render={record =>
                            record.storages && record.storages.length > 0
                                ? record.storages[0].name || ''
                                : ''
                        }
                    />
                );
            default:
                return null;
        }
    };

    return (
        <CustomizableGrid
            title="StorageTypes"
            defaultColumns={defaultColumns}
            storageKey="storage-types-columns"
            renderColumn={renderColumn}
            bulkActionButtons={false}
            filtersComponent={<StorageTypeFilter />}
            sort={{ field: 'id', order: 'DESC' }}
            paginationComponent={<BoxitPagination />}
            actions={<ListActions />}
            perPage={50}
            permissions={[]}
            {...props}
        />
    );
};

StorageTypeList.defaultProps = {
    resource: 'storage-types',
    location: { search: '' },
};

export default StorageTypeList;
